item
<template>
  <!-- ======================= -->
  <!-- TAB 2: ARTEFACT         -->
  <!-- ======================= -->
  <div class="row mt-2 mb-1 align-items-center">
    <!-- ------------------- -->
    <!--   ARTEFACT DETAILS  -->
    <!-- ------------------- -->
    <div class="col">
      <p class="bb-hdg mt-3 mb-2">{{ detailsHeading }}</p>
    </div>
    <div
      v-if="isItemCardMode"
      class="col-auto d-flex align-items-center gap-2"
    >
      <!-- --------------------------------- -->
      <!--   new or dropped artefact switch  -->
      <!-- --------------------------------- -->
      <div
        class="me-3"
        v-if="showDroppedArtefactToggle"
      >
        <enhanced-toggle
          style="
            --toggleWidth: 300px;
            --labelWidth: 200px;
            --switchWidth: 90px;
            --switchPos: 210px;
            height: 50px;
            border-radius: 10px;
            background-color: white;
          "
          :styleOff="`default`"
          :styleOn="`primary`"
          :labelOn="`Dropped Artefact`"
          :labelOff="`New Artefact`"
          :checked="isDroppedNonCampaignArtefactMode"
          @update:modelValue="onDroppedArtefactToggle"
        />
      </div>
      <!-- --------------------------- -->
      <!--   campaign artefact switch  -->
      <!-- --------------------------- -->
      <div v-if="showCampaignArtefactToggle">
        <enhanced-toggle
          style="
            --toggleWidth: 270px;
            --labelWidth: 180px;
            --switchWidth: 100px;
            --switchPos: 190px;
            height: 50px;
            border-radius: 10px;
            background-color: white;
          "
          :styleOff="`default`"
          :styleOn="`primary`"
          :labelOn="`Campaign`"
          :labelOff="`Non-Campaign`"
          :checked="!!(localItem.campaign ?? activeItem.campaign)"
          @update:modelValue="onCampaignItemToggle"
        />
      </div>
    </div>
  </div>
  <!-- =================================================================================== -->
  <!--  where NOT campaign item  -->
  <!-- ------------------------- -->
  <template v-if="isItemCardNonCampaignMode">
    <!-- ---------- -->
    <!--    ROW 2   -->
    <!-- ---------- -->
    <div
      v-if="!showDroppedArtefactSelect || artefactSelectionReady"
      class="row mt-2"
    >
      <!-- ---------------------- -->
      <!--  artefact type (input) -->
      <!-- ---------------------- -->
      <div
        v-if="isItemCardArtefactTypeEditable"
        class="col-8"
      >
        <div class="form-group">
          <div class="form-floating">
            <v-select
              :options="artefactTypes"
              class="form-control"
              placeholder="select..."
              :modelValue="localItem.artefact_type"
              @keydown="onKeyDown($event)"
              :class="{ 'is-invalid': errorFields.includes('artefact_type') }"
              @click="clearInputError('artefact_type')"
              @update:modelValue="
                onArtefactFieldChange('artefact_type', $event)
              "
              id="artefact_type"
            >
              <template #search="{ attributes, events }">
                <input
                  class="vs__search"
                  v-bind="attributes"
                  v-on="events"
                />
              </template>
            </v-select>
            <label
              class="bb-float-label"
              for="artefact_type"
              >Artefact Type</label
            >
          </div>
        </div>
      </div>
      <!-- --------------------- -->
      <!--  artefact type (r/o)  -->
      <!-- --------------------- -->
      <div
        v-else
        class="col-8 form-group"
      >
        <div class="form-floating">
          <input
            readonly
            class="form-control text-left"
            :value="activeItem.artefact_type"
            id="artefact_type"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="artefact_type"
            >Artefact Type</label
          >
        </div>
      </div>
      <!-- ------------------------ -->
      <!--  pwr storage (counter)   -->
      <!-- ------------------------ -->
      <div
        v-if="artefactSelectionReady && !effectiveCampaign"
        class="col-4"
      >
        <CounterEmit
          ref="pwrStorageCounter"
          :pass_ref="'pwr_storage'"
          :pass_label="'Total Pwr Storage'"
          :pass_count="localItem.pwr_storage ?? activeItem.pwr_storage ?? 0"
          :pass_min="0"
          :pass_max="20"
          :pass_digits="2"
          @change="onArtefactFieldChange('pwr_storage', $event.count)"
        />
      </div>
    </div>
    <div
      v-if="showEditableArtefactBaseItemSelect"
      class="row mt-2 mb-2"
    >
      <div class="col-8 form-group">
        <div class="form-floating">
          <v-select
            :options="artefactBaseItems"
            class="form-control"
            placeholder="select..."
            :modelValue="localItem.artefact_base_item"
            :get-option-label="getArtefactBaseItemLabel"
            :reduce="reduceObject"
            @keydown="onKeyDown($event)"
            :class="{
              'is-invalid': errorFields.includes('artefact_base_item'),
            }"
            @click="clearInputError('artefact_base_item')"
            @update:modelValue="
              onArtefactFieldChange('artefact_base_item', $event)
            "
            id="artefact_base_item"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                v-bind="attributes"
                v-on="events"
              />
            </template>
          </v-select>
          <label
            class="bb-float-label optional"
            for="artefact_base_item"
            >Base Item</label
          >
        </div>
      </div>
    </div>
    <div
      v-else-if="showReadonlyArtefactBaseItem"
      class="row mt-2 mb-2"
    >
      <div class="col-8 form-group">
        <div class="form-floating">
          <input
            readonly
            class="form-control text-left"
            :value="readonlyArtefactBaseItemLabel"
            id="artefact_base_item"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="artefact_base_item"
            >Base Item</label
          >
        </div>
      </div>
    </div>
    <div
      v-if="
        artefactSelectionReady && !isCampaignArmourOrWeaponBasicPwrStorageHidden
      "
      class="row mt-2 mb-2"
    >
      <!-- ----------------- -->
      <!--  material (input) -->
      <!-- ----------------- -->
      <div
        v-if="isDescriptorEditable('material')"
        class="col-5"
      >
        <div class="form-group">
          <div class="form-floating">
            <v-select
              :options="materials"
              class="form-control"
              placeholder="select..."
              :modelValue="localItem.material"
              @keydown="onKeyDown($event)"
              :class="{ 'is-invalid': errorFields.includes('material') }"
              @click="clearInputError('material')"
              @update:modelValue="onArtefactFieldChange('material', $event)"
              id="material"
            >
              <template #search="{ attributes, events }">
                <input
                  class="vs__search"
                  v-bind="attributes"
                  v-on="events"
                />
              </template>
            </v-select>
            <label
              class="bb-float-label optional"
              for="material"
              >Material</label
            >
          </div>
        </div>
      </div>
      <!-- ---------------- -->
      <!--  material (r/o)  -->
      <!-- ---------------- -->
      <div
        v-else
        class="col-5 form-group"
      >
        <div class="form-floating">
          <input
            readonly
            class="form-control text-left"
            :value="activeItem.material"
            id="material"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="material"
            >Material</label
          >
        </div>
      </div>
      <!-- ---------------- -->
      <!--  quality (input) -->
      <!-- ---------------- -->
      <div
        v-if="isDescriptorEditable('quality')"
        class="col-5"
      >
        <div class="form-group">
          <div class="form-floating">
            <v-select
              :options="qualities"
              class="form-control"
              placeholder="select..."
              :modelValue="localItem.quality"
              @keydown="onKeyDown($event)"
              :class="{ 'is-invalid': errorFields.includes('quality') }"
              @click="clearInputError('quality')"
              @update:modelValue="onArtefactFieldChange('quality', $event)"
              id="quality"
            >
              <template #search="{ attributes, events }">
                <input
                  class="vs__search"
                  v-bind="attributes"
                  v-on="events"
                />
              </template>
            </v-select>
            <label
              class="bb-float-label optional"
              for="quality"
              >Quality</label
            >
          </div>
        </div>
      </div>
      <!-- --------------- -->
      <!--  quality (r/o)  -->
      <!-- --------------- -->
      <div
        v-else
        class="col-5 form-group"
      >
        <div class="form-floating">
          <input
            readonly
            class="form-control text-left"
            :value="activeItem.quality"
            id="quality"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="quality"
            >Quality</label
          >
        </div>
      </div>
    </div>
  </template>
  <!-- =================================================================================== -->
  <!--  where campaign item  -->
  <!-- --------------------- -->
  <template v-else-if="isItemCardCampaignMode">
    <div class="row mt-2 mb-2">
      <div
        :class="
          hasSelectedCampaignArtefact
            ? showCampaignRef
              ? 'col-6'
              : 'col-8'
            : showCampaignRef
              ? 'col-7'
              : 'col-9'
        "
        class="form-group"
      >
        <div
          v-if="activeItem.id === 0"
          class="form-floating"
        >
          <v-select
            :modelValue="localItem.name"
            :options="itemNames"
            label="label"
            :reduce="(option) => option.value"
            class="form-control"
            :class="{ 'is-invalid': errorFields.includes('name') }"
            @keydown="onKeyDown($event)"
            @update:modelValue="onItemSelect"
            id="campaign_artefact_name"
            @click="clearInputError('name')"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                :required="!localItem.name"
                v-bind="attributes"
                v-on="events"
              />
            </template>
          </v-select>
          <label
            class="bb-float-label"
            for="campaign_artefact_name"
            >Name</label
          >
        </div>
        <div
          v-else
          class="form-floating"
        >
          <input
            readonly
            class="form-control text-left"
            :value="itemName"
            id="campaign_artefact_name"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="campaign_artefact_name"
            >Name</label
          >
        </div>
      </div>
      <div
        v-if="showCampaignRef"
        class="col-3 form-group"
      >
        <div class="form-floating">
          <input
            readonly
            class="form-control text-center"
            :value="campaignRefValue"
            id="campaign_ref"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="campaign_ref"
            >Campaign Ref</label
          >
        </div>
      </div>
      <div
        v-if="showCampaignSelectedPwrStorageField"
        class="col-4"
      >
        <div
          v-if="isCampaignSelectedPwrStorageReadonly"
          class="form-group"
        >
          <div class="form-floating">
            <input
              readonly
              class="form-control text-center"
              :value="localItem.pwr_storage ?? activeItem.pwr_storage ?? 0"
              id="pwr_storage_readonly"
              disabled
            />
            <label
              class="bb-float-label optional"
              for="pwr_storage_readonly"
              >Total Pwr Storage</label
            >
          </div>
        </div>
        <CounterEmit
          v-else
          ref="pwrStorageCounter"
          :pass_ref="'pwr_storage'"
          :pass_label="'Total Pwr Storage'"
          :pass_count="localItem.pwr_storage ?? activeItem.pwr_storage ?? 0"
          :pass_min="0"
          :pass_max="20"
          :pass_digits="2"
          @change="onArtefactFieldChange('pwr_storage', $event.count)"
        />
      </div>
    </div>
  </template>
  <template v-else-if="isArtefactCardCampaignMode">
    <!-- ---------- -->
    <!--    ROW 3   -->
    <!-- ---------- -->
    <div class="row mt-2 mb-2">
      <div
        v-if="isEditable(localItem.artefact_type)"
        class="col-8 mb-2"
      >
        <div class="form-group">
          <div class="form-floating">
            <v-select
              :options="artefactTypes"
              class="form-control"
              placeholder="select..."
              :modelValue="localItem.artefact_type"
              @keydown="onKeyDown($event)"
              :class="{ 'is-invalid': errorFields.includes('artefact_type') }"
              @click="clearInputError('artefact_type')"
              @update:modelValue="
                onArtefactFieldChange('artefact_type', $event)
              "
              id="artefact_type_campaign"
            >
              <template #search="{ attributes, events }">
                <input
                  class="vs__search"
                  v-bind="attributes"
                  v-on="events"
                />
              </template>
            </v-select>
            <label
              class="bb-float-label"
              for="artefact_type_campaign"
              >Artefact Type</label
            >
          </div>
        </div>
      </div>
      <div
        v-else
        class="col-8 form-group mb-2"
      >
        <div class="form-floating">
          <input
            readonly
            class="form-control text-left"
            :value="activeItem.artefact_type"
            id="artefact_type_campaign"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="artefact_type_campaign"
            >Artefact Type</label
          >
        </div>
      </div>
      <div
        v-if="showCampaignRef"
        class="col-3 form-group mb-2"
      >
        <div class="form-floating">
          <input
            readonly
            class="form-control text-center"
            :value="campaignRefValue"
            id="campaign_ref_artefact"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="campaign_ref_artefact"
            >Campaign Ref</label
          >
        </div>
      </div>
      <div
        v-if="artefactSelectionReady"
        class="col-8 form-group"
      >
        <div
          v-if="!isOwnedByCharacter"
          class="form-floating"
        >
          <v-select
            :options="filteredItemSizes"
            class="form-control"
            placeholder="select..."
            :modelValue="localItem.item_size"
            :get-option-label="getItemSizeLabel"
            :reduce="reduceObject"
            @keydown="onKeyDown($event)"
            :class="{ 'is-invalid': errorFields.includes('item_size') }"
            @click="clearInputError('item_size')"
            @update:modelValue="onArtefactFieldChange('item_size', $event)"
            id="item_size"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                v-bind="attributes"
                v-on="events"
              />
            </template>
          </v-select>
          <label
            class="bb-float-label"
            for="item_size"
            >Artefact Size</label
          >
        </div>
        <div
          v-else
          class="form-floating"
        >
          <input
            id="item_size"
            class="form-control text-left"
            :value="readonlyItemSizeName"
            readonly
            disabled
            placeholder="..."
          />
          <label
            class="bb-float-label optional"
            for="item_size"
            >Artefact Size</label
          >
        </div>
      </div>
    </div>
    <div
      v-if="
        artefactSelectionReady &&
        showArtefactBaseItemSelect &&
        !isOwnedByCharacter
      "
      class="row mt-2 mb-2"
    >
      <div class="col-8 form-group">
        <div class="form-floating">
          <v-select
            :options="artefactBaseItems"
            class="form-control"
            placeholder="select..."
            :modelValue="localItem.artefact_base_item"
            :get-option-label="getArtefactBaseItemLabel"
            :reduce="reduceObject"
            @keydown="onKeyDown($event)"
            :class="{
              'is-invalid': errorFields.includes('artefact_base_item'),
            }"
            @click="clearInputError('artefact_base_item')"
            @update:modelValue="
              onArtefactFieldChange('artefact_base_item', $event)
            "
            id="artefact_base_item"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                v-bind="attributes"
                v-on="events"
              />
            </template>
          </v-select>
          <label
            class="bb-float-label optional"
            for="artefact_base_item"
            >Base Item</label
          >
        </div>
      </div>
    </div>
    <div
      v-if="artefactSelectionReady && showContainerSizeSelect"
      class="row mt-2 mb-2"
    >
      <div class="col-9 form-group">
        <div class="form-floating">
          <v-select
            :options="containerSizes"
            class="form-control"
            placeholder="select..."
            :modelValue="localItem.container_size"
            :get-option-label="getContainerSizeLabel"
            :reduce="reduceObject"
            @keydown="onKeyDown($event)"
            :class="{ 'is-invalid': errorFields.includes('container_size') }"
            @click="clearInputError('container_size')"
            @update:modelValue="onArtefactFieldChange('container_size', $event)"
            id="container_size"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                v-bind="attributes"
                v-on="events"
              />
            </template>
          </v-select>
          <label
            class="bb-float-label optional"
            for="container_size"
            >Container Size</label
          >
        </div>
      </div>
    </div>
    <div
      v-if="artefactSelectionReady"
      class="row mt-2 mb-2"
    >
      <!-- ------------------------ -->
      <!--  pwr storage (counter)   -->
      <!-- ------------------------ -->
      <div class="col-4">
        <CounterEmit
          ref="pwrStorageCounter"
          :pass_ref="'pwr_storage'"
          :pass_label="'Tot Pwr Storage'"
          :pass_count="localItem.pwr_storage ?? activeItem.pwr_storage ?? 0"
          :pass_min="0"
          :pass_max="20"
          :pass_digits="2"
          @change="onArtefactFieldChange('pwr_storage', $event.count)"
        />
      </div>
    </div>
    <!-- ---------- -->
    <!--    ROW 4   -->
    <!-- ---------- -->
    <div
      v-if="artefactSelectionReady"
      class="row mt-2 mb-2"
    >
      <!-- ------------------ -->
      <!--  item type (r/o)   -->
      <!-- ------------------ -->
      <div
        v-if="showReadonlyItemType"
        class="col-4 form-group"
      >
        <div class="form-floating">
          <input
            readonly
            class="form-control text-left"
            :value="readonlyItemType"
            id="item_type"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="item_type"
            >{{ activeItem.artefact_type }} Type</label
          >
        </div>
      </div>
    </div>
    <!-- ---------- -->
    <!--    ROW 5   -->
    <!-- ---------- -->
    <div
      v-if="artefactSelectionReady"
      class="row mt-2 mb-2"
    >
      <!-- ----------------- -->
      <!--  material (input) -->
      <!-- ----------------- -->
      <div
        v-if="effectiveCampaign && isDescriptorEditable('material')"
        class="col-5"
      >
        <div class="form-group">
          <div class="form-floating">
            <v-select
              :options="materials"
              class="form-control"
              placeholder="select..."
              :modelValue="localItem.material"
              @keydown="onKeyDown($event)"
              :class="{ 'is-invalid': errorFields.includes('material') }"
              @click="clearInputError('material')"
              @update:modelValue="onArtefactFieldChange('material', $event)"
              id="material"
            >
              <template #search="{ attributes, events }">
                <input
                  class="vs__search"
                  v-bind="attributes"
                  v-on="events"
                />
              </template>
            </v-select>
            <label
              class="bb-float-label optional"
              for="material"
              >Material</label
            >
          </div>
        </div>
      </div>
      <!-- ---------------- -->
      <!--  material (r/o)  -->
      <!-- ---------------- -->
      <div
        v-else
        class="col-5 form-group"
      >
        <div class="form-floating">
          <input
            readonly
            class="form-control text-left"
            :value="activeItem.material"
            id="material"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="material"
            >Material</label
          >
        </div>
      </div>
      <!-- ---------------- -->
      <!--  quality (input) -->
      <!-- ------------------->
      <div
        v-if="isDescriptorEditable('quality')"
        class="col-5"
      >
        <div class="form-group">
          <div class="form-floating">
            <v-select
              :options="qualities"
              class="form-control"
              placeholder="select..."
              :modelValue="localItem.quality"
              @keydown="onKeyDown($event)"
              :class="{ 'is-invalid': errorFields.includes('quality') }"
              @click="clearInputError('quality')"
              @update:modelValue="onArtefactFieldChange('quality', $event)"
              id="quality"
            >
              <template #search="{ attributes, events }">
                <input
                  class="vs__search"
                  v-bind="attributes"
                  v-on="events"
                />
              </template>
            </v-select>
            <label
              class="bb-float-label optional"
              for="quality"
              >Quality</label
            >
          </div>
        </div>
      </div>
      <!-- --------------- -->
      <!--  quality (r/o)  -->
      <!-- --------------- -->
      <div
        v-else
        class="col-5 form-group"
      >
        <div class="form-floating">
          <input
            readonly
            class="form-control text-left"
            :value="activeItem.quality"
            id="quality"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="quality"
            >Quality</label
          >
        </div>
      </div>
    </div>
  </template>
  <div
    v-if="isItemCardCampaignArtefactSelected"
    class="row mt-2 mb-2"
  >
    <!-- ----------------- -->
    <!--  material (input) -->
    <!-- ----------------- -->
    <div
      v-if="isDescriptorEditable('material')"
      class="col-5"
    >
      <div class="form-group">
        <div class="form-floating">
          <v-select
            :options="materials"
            class="form-control"
            placeholder="select..."
            :modelValue="localItem.material"
            @keydown="onKeyDown($event)"
            :class="{ 'is-invalid': errorFields.includes('material') }"
            @click="clearInputError('material')"
            @update:modelValue="onArtefactFieldChange('material', $event)"
            id="material_campaign_item"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                v-bind="attributes"
                v-on="events"
              />
            </template>
          </v-select>
          <label
            class="bb-float-label optional"
            for="material_campaign_item"
            >Material</label
          >
        </div>
      </div>
    </div>
    <div
      v-else
      class="col-5 form-group"
    >
      <div class="form-floating">
        <input
          readonly
          class="form-control text-left"
          :value="activeItem.material"
          id="material_campaign_item"
          disabled
        />
        <label
          class="bb-float-label optional"
          for="material_campaign_item"
          >Material</label
        >
      </div>
    </div>

    <!-- ---------------- -->
    <!--  quality (input) -->
    <!-- ---------------- -->
    <div
      v-if="isDescriptorEditable('quality')"
      class="col-5"
    >
      <div class="form-group">
        <div class="form-floating">
          <v-select
            :options="qualities"
            class="form-control"
            placeholder="select..."
            :modelValue="localItem.quality"
            @keydown="onKeyDown($event)"
            :class="{ 'is-invalid': errorFields.includes('quality') }"
            @click="clearInputError('quality')"
            @update:modelValue="onArtefactFieldChange('quality', $event)"
            id="quality_campaign_item"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                v-bind="attributes"
                v-on="events"
              />
            </template>
          </v-select>
          <label
            class="bb-float-label optional"
            for="quality_campaign_item"
            >Quality</label
          >
        </div>
      </div>
    </div>
    <div
      v-else
      class="col-5 form-group"
    >
      <div class="form-floating">
        <input
          readonly
          class="form-control text-left"
          :value="activeItem.quality"
          id="quality_campaign_item"
          disabled
        />
        <label
          class="bb-float-label optional"
          for="quality_campaign_item"
          >Quality</label
        >
      </div>
    </div>
  </div>
  <!-- =================================================================================== -->
  <!--    ROW 6   -->
  <!-- ---------- -->
  <div
    v-if="
      (isItemCardNonCampaignMode && artefactSelectionReady) ||
      isItemCardCampaignArtefactSelected ||
      (isArtefactCardCampaignMode && artefactSelectionReady)
    "
    class="row mt-2 mb-2"
  >
    <!-- ------------ -->
    <!--  age (input) -->
    <!-- --------------->
    <div
      v-if="isDescriptorEditable('age')"
      class="col-5"
    >
      <div class="form-group">
        <div class="form-floating">
          <v-select
            :options="ages"
            class="form-control"
            placeholder="select..."
            :modelValue="localItem.age"
            @keydown="onKeyDown($event)"
            :class="{ 'is-invalid': errorFields.includes('age') }"
            @click="clearInputError('age')"
            @update:modelValue="onArtefactFieldChange('age', $event)"
            id="age"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                v-bind="attributes"
                v-on="events"
              />
            </template>
          </v-select>
          <label
            class="bb-float-label optional"
            for="age"
            >Age</label
          >
        </div>
      </div>
    </div>
    <!-- ---------- -->
    <!--  age (r/o)  -->
    <!-- ----------- -->
    <div
      v-else
      class="col-5 form-group"
    >
      <div class="form-floating">
        <input
          readonly
          class="form-control text-left"
          :value="activeItem.age"
          id="age"
          disabled
        />
        <label
          class="bb-float-label optional"
          for="age"
          >Age</label
        >
      </div>
    </div>
    <!-- -------------- -->
    <!--  race (input)  -->
    <!-- ----------------->
    <div
      v-if="isDescriptorEditable('race')"
      class="col-5"
    >
      <div class="form-group">
        <div class="form-floating">
          <v-select
            :options="raceOptions"
            class="form-control"
            placeholder="Unknown"
            v-model="raceSelection"
            :get-option-label="getRaceLabel"
            @keydown="onKeyDown($event)"
            :class="{ 'is-invalid': errorFields.includes('race') }"
            @click="clearInputError('race')"
            id="race"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                v-bind="attributes"
                v-on="events"
              />
            </template>
          </v-select>
          <label
            class="bb-float-label optional"
            for="race"
            >Race</label
          >
        </div>
      </div>
    </div>
    <!-- ----------- -->
    <!--  race (r/o)  -->
    <!-- ------------ -->
    <div
      v-else
      class="col-5 form-group"
    >
      <div class="form-floating">
        <input
          readonly
          class="form-control text-left"
          :value="readonlyRaceName"
          id="race"
          disabled
        />
        <label
          class="bb-float-label optional"
          for="race"
          >Race</label
        >
      </div>
    </div>
  </div>
  <!-- =================================================================================== -->
  <!--    ROW 7   -->
  <!-- ---------- -->
  <div
    v-if="
      (isItemCardNonCampaignMode &&
        (artefactSelectionReady || showDroppedArtefactSelect)) ||
      (isArtefactCardCampaignMode && artefactSelectionReady)
    "
    class="row mt-2 mb-2"
  >
    <!-- ----------------------- -->
    <!--  name (input / output) -->
    <!-- ----------------------- -->
    <div
      v-if="showDroppedArtefactSelect"
      class="col-8 form-group"
    >
      <div class="form-floating">
        <v-select
          :modelValue="localItem.name"
          :options="droppedArtefactOptions"
          label="label"
          :reduce="(option) => option.value"
          class="form-control"
          :class="{ 'is-invalid': errorFields.includes('name') }"
          @keydown="onKeyDown($event)"
          @update:modelValue="onItemSelect"
          id="dropped_artefact_name"
          @click="clearInputError('name')"
        >
          <template #search="{ attributes, events }">
            <input
              class="vs__search"
              :required="!localItem.name"
              v-bind="attributes"
              v-on="events"
            />
          </template>
        </v-select>
        <label
          class="bb-float-label"
          for="dropped_artefact_name"
          >Name</label
        >
      </div>
    </div>
    <div
      v-else-if="
        isArtefactCardCampaignMode ||
        (isItemCardNonCampaignMode &&
          (isItemCardNonCampaignMode ||
            effectiveCampaign ||
            isEditable(localItem.name)))
      "
      class="col-8 form-group"
    >
      <div class="form-floating">
        <input
          class="form-control text-left"
          :value="localItem.name"
          id="name"
          @input="onNameInput($event)"
        />
        <label
          class="bb-float-label optional"
          for="name"
          >Name</label
        >
      </div>
    </div>
    <!-- -------------- -->
    <!--  name (r/o)    -->
    <!-- ----------------->
    <div
      v-else
      class="col-10 form-group"
    >
      <div class="form-floating">
        <input
          readonly
          class="form-control text-left"
          :value="activeItem.name"
          id="name"
          disabled
        />
        <label
          class="bb-float-label"
          for="name"
          >Name</label
        >
      </div>
    </div>
  </div>
  <!-- =================================================================================== -->
  <!--    ROW 8   -->
  <!-- ---------- -->
  <div
    v-if="showValueSection"
    class="row"
  >
    <!-- ------------ -->
    <!--    divider   -->
    <!-- ------------ -->
    <hr class="bb-horizontal-divider" />
    <!-- --------------------- -->
    <!--  lower value (input)  -->
    <!-- --------------------- -->
    <div class="col-2 form-group">
      <div
        v-if="areValueFieldsLocked"
        class="form-floating"
      >
        <input
          readonly
          class="form-control text-center"
          :class="{ 'is-invalid': errorFields.includes('value_lower') }"
          :value="displayValueLower"
          id="value_lower"
          disabled
        />
        <label
          class="bb-float-label optional"
          for="value_lower"
          >Val (Lo)</label
        >
      </div>
      <div
        v-else
        class="form-floating"
      >
        <input
          class="form-control text-center"
          :class="{ 'is-invalid': errorFields.includes('value_lower') }"
          :value="displayValueLower"
          id="value_lower"
          @input="
            handleNumericInput({
              displayProp: 'displayValueLower',
              emitField: 'value_lower',
              event: $event,
              min: 0,
              max: 999999,
              zeroIfBlank: false,
            })
          "
        />
        <label
          class="bb-float-label optional"
          for="value_lower"
          >Val (Lo)</label
        >
      </div>
    </div>
    <!-- --------------------- -->
    <!--  upper value (input)  -->
    <!-- --------------------- -->
    <div class="col-2 form-group">
      <div
        v-if="areValueFieldsLocked"
        class="form-floating"
      >
        <input
          readonly
          class="form-control text-center"
          :class="{ 'is-invalid': errorFields.includes('value_upper') }"
          :value="displayValueUpper"
          id="value_upper"
          disabled
        />
        <label
          class="bb-float-label optional"
          for="value_upper"
          >Val (Hi)</label
        >
      </div>
      <div
        v-else
        class="form-floating"
      >
        <input
          class="form-control text-center"
          :class="{ 'is-invalid': errorFields.includes('value_upper') }"
          :value="displayValueUpper"
          id="value_upper"
          @input="
            handleNumericInput({
              displayProp: 'displayValueUpper',
              emitField: 'value_upper',
              event: $event,
              min: 0,
              max: 999999,
              zeroIfBlank: false,
            })
          "
        />
        <label
          class="bb-float-label optional"
          for="value_upper"
          >Val (Hi)</label
        >
      </div>
    </div>
    <!-- ------------------ -->
    <!--  currency (input)  -->
    <!-- --------------------->
    <div
      v-if="gotValue"
      class="col-3"
    >
      <div
        v-if="areValueFieldsLocked"
        class="form-group"
      >
        <div class="form-floating">
          <input
            readonly
            class="form-control text-center"
            :value="readonlyCurrencyName"
            id="currency"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="currency"
            >Currency</label
          >
        </div>
      </div>
      <!-- editable select for all other cases -->
      <div
        v-else
        class="form-group"
      >
        <div class="form-floating">
          <v-select
            :options="currencies"
            class="form-control"
            placeholder="Select..."
            :modelValue="selectedCurrencyValue"
            :get-option-label="getCurrencyLabel"
            :reduce="reduceObject"
            @keydown="onKeyDown($event)"
            :class="{ 'is-invalid': errorFields.includes('currency') }"
            @click="clearInputError('currency')"
            @update:modelValue="onArtefactFieldChange('currency', $event)"
            id="currency"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                v-bind="attributes"
                v-on="events"
              />
            </template>
          </v-select>
          <label
            class="bb-float-label optional"
            for="currency"
            >Currency</label
          >
        </div>
      </div>
    </div>
    <!-- -------------------------- -->
    <!--  estimated value (switch)  -->
    <!-- -------------------------- -->
    <template v-if="gotValue">
      <div class="col-auto pe-3">
        <div
          :style="
            isEstimatedValueToggleReadonly
              ? 'pointer-events: none; opacity: 0.5;'
              : ''
          "
        >
          <enhanced-toggle
            style="
              --toggleWidth: 220px;
              --labelWidth: 160px;
              --switchWidth: 90px;
              --switchPos: 160px;
              height: 50px;
              border-radius: 10px;
              background-color: white;
            "
            :styleOff="`default`"
            :styleOn="`primary`"
            :labelOn="`Estimated`"
            :labelOff="`Actual Value`"
            :checked="activeItem.estimated_value"
            @update:modelValue="onEstimatedValueToggle"
          >
          </enhanced-toggle>
        </div>
      </div>
    </template>
  </div>
  <!-- --------- -->
  <!--   ROW 9   -->
  <!-- --------- -->
  <div
    v-if="
      (isItemCardNonCampaignMode && artefactSelectionReady) ||
      isItemCardCampaignArtefactSelected
    "
    class="row mt-2 mb-2"
  >
    <!-- ------------ -->
    <!--    divider   -->
    <!-- ------------ -->
    <hr class="bb-horizontal-divider" />
    <!-- ---------------------------- -->
    <!--  short description (input)   -->
    <!-- ---------------------------- -->
    <div class="col form-group">
      <div class="form-floating">
        <input
          id="short_description"
          name="short_description"
          class="form-control text-left bb-card-short-description-control"
          :value="activeItem.short_description"
          :readonly="isItemCardCampaignShortDescriptionReadonly"
          :disabled="isItemCardCampaignShortDescriptionReadonly"
          placeholder="..."
          maxlength="100"
          @input="
            !isItemCardCampaignShortDescriptionReadonly &&
            onCapitaliseFirstLetter('short_description', $event)
          "
        />
        <label
          class="bb-float-label optional"
          for="short_description"
          >Short Description</label
        >
      </div>
    </div>
  </div>
  <!-- =================================================================================== -->
  <!--   ROW 10  -->
  <!-- --------- -->
  <div
    v-if="
      (isItemCardNonCampaignMode && artefactSelectionReady) ||
      isItemCardCampaignArtefactSelected
    "
    class="row mt-2 mb-2"
  >
    <!-- ----------------------------- -->
    <!--  description (input or r/o)   -->
    <!-- ----------------------------- -->
    <div class="col form-group">
      <div class="form-floating">
        <textarea
          id="description"
          name="description"
          type="text"
          rows="4"
          placeholder="..."
          class="form-control bb-textarea-with-label bb-card-description-control bb-textarea-with-label-compact"
          :value="activeItem.description"
          :readonly="isItemCardCampaignArtefactSelection"
          :disabled="isItemCardCampaignArtefactSelection"
          autocomplete="off"
          @input="
            !isItemCardCampaignArtefactSelection && onDescriptionInput($event)
          "
        ></textarea>
        <label
          class="bb-float-label optional"
          for="description"
          >Description</label
        >
      </div>
    </div>
    <!-- ---------------- -->
    <!--  notes (input)   -->
    <!-- ---------------- -->
    <div class="col form-group">
      <div class="form-floating">
        <textarea
          id="notes"
          name="notes"
          type="text"
          rows="4"
          placeholder="..."
          class="form-control bb-textarea-with-label bb-card-description-control bb-textarea-with-label-compact"
          :value="activeItem.player_notes"
          autocomplete="off"
          @input="onNotesInput($event)"
        ></textarea>
        <label
          class="bb-float-label optional"
          for="notes"
          >Player Notes</label
        >
      </div>
    </div>
  </div>
  <!-- ========================== -->
  <!--   CHARACTER LINK (r/o)     -->
  <!-- ========================== -->
  <template
    v-if="
      isArtefactCardCampaignMode &&
      (activeItem.character_name || activeItem.player_name)
    "
  >
    <div class="row mt-3 mb-1">
      <hr class="bb-horizontal-divider" />
    </div>
    <div class="row mt-2 mb-2">
      <div class="col form-group">
        <div class="form-floating">
          <input
            id="player_name_readonly"
            name="player_name_readonly"
            class="form-control text-left"
            :value="activeItem.player_name ?? ''"
            readonly
            disabled
            placeholder="..."
          />
          <label
            class="bb-float-label"
            for="player_name_readonly"
            >Owning Player</label
          >
        </div>
      </div>
      <div class="col form-group">
        <div class="form-floating">
          <input
            id="character_name_readonly"
            name="character_name_readonly"
            class="form-control text-left"
            :value="activeItem.character_name ?? ''"
            readonly
            disabled
            placeholder="..."
          />
          <label
            class="bb-float-label"
            for="character_name_readonly"
            >Owning Character</label
          >
        </div>
      </div>
    </div>
  </template>
</template>

<script>
// eslint-disable vue/no-mutating-props
import { EnhancedToggle } from 'vue-enhanced-check'

const UNKNOWN_RACE_OPTION_ID = '__unknown_race__'

function defaultItemSizeLabel(option) {
  if (!option || typeof option !== 'object') {
    return ''
  }

  const sizeShort = option.size_short || option.size || ''
  const enc = Number(option.enc)

  if (Number.isNaN(enc)) {
    return `Size: ${sizeShort}`
  }

  return `Size: ${sizeShort} - ENC: ${enc}`
}

import CounterEmit from './CounterEmit.vue'

export default {
  name: 'ItemArtefactTab',
  components: {
    EnhancedToggle,
    CounterEmit,
  },
  props: {
    activeItem: {
      type: Object,
      required: true,
    },
    localItem: {
      type: Object,
      required: true,
    },
    forceCampaignLayout: {
      type: Boolean,
      default: false,
    },
    isCampaignArtefact: {
      type: Boolean,
      default: false,
    },
    shortDescriptionReadonly: {
      type: Boolean,
      default: false,
    },
    artefactTypes: {
      type: Array,
      required: true,
    },
    artefactBaseItems: {
      type: Array,
      default: () => [],
    },
    itemSizes: {
      type: Array,
      default: () => [],
    },
    containerSizes: {
      type: Array,
      default: () => [],
    },
    itemName: {
      type: String,
      required: true,
    },
    detailsHeading: {
      type: String,
      default: 'Artefact Details',
    },
    itemNames: {
      type: Array,
      default: () => [],
    },
    droppedArtefactOptions: {
      type: Array,
      default: () => [],
    },
    materials: {
      type: Array,
      required: true,
    },
    qualities: {
      type: Array,
      required: true,
    },
    ages: {
      type: Array,
      required: true,
    },
    races: {
      type: Array,
      required: true,
    },
    currencies: {
      type: Array,
      required: true,
    },
    errorFields: {
      type: Array,
      required: true,
    },
    showCampaignRef: {
      type: Boolean,
      default: false,
    },
    campaignRefValue: {
      type: [String, Number],
      default: '',
    },
    displayValueLower: {
      type: [String, Number],
      required: true,
    },
    displayValueUpper: {
      type: [String, Number],
      required: true,
    },
    readonlyRaceName: {
      type: String,
      required: true,
    },
    gotValue: {
      type: Boolean,
      required: true,
    },
    // handlers / helpers passed from parent
    isEditable: {
      type: Function,
      required: true,
    },
    // handlers / helpers passed from parent
    isNewItem: {
      type: Boolean,
      required: true,
    },
    onKeyDown: {
      type: Function,
      required: true,
    },
    clearInputError: {
      type: Function,
      required: true,
    },
    onItemSelect: {
      type: Function,
      default: null,
    },
    onArtefactFieldChange: {
      type: Function,
      required: true,
    },
    onNameInput: {
      type: Function,
      required: true,
    },
    onDescriptionInput: {
      type: Function,
      required: true,
    },
    onNotesInput: {
      type: Function,
      required: true,
    },
    getRaceLabel: {
      type: Function,
      required: true,
    },
    getArtefactBaseItemLabel: {
      type: Function,
      default: (option) => option?.name ?? '',
    },
    getItemSizeLabel: {
      type: Function,
      default: defaultItemSizeLabel,
    },
    getContainerSizeLabel: {
      type: Function,
      default: (option) => option?.name ?? '',
    },
    getCurrencyLabel: {
      type: Function,
      required: true,
    },
    reduceObject: {
      type: Function,
      required: true,
    },
    handleNumericInput: {
      type: Function,
      required: true,
    },
    onEstimatedValueToggle: {
      type: Function,
      required: true,
    },
    onCampaignItemToggle: {
      type: Function,
      default: null,
    },
    onDroppedArtefactToggle: {
      type: Function,
      default: null,
    },
    delayedEmitUpdate: {
      type: Function,
      required: true,
    },
    onCapitaliseFirstLetter: {
      type: Function,
      required: true,
    },
  },
  methods: {
    normaliseCurrencyValue(raw) {
      if (raw === null || raw === undefined || raw === '') {
        return null
      }

      if (typeof raw === 'object') {
        return raw.id ?? null
      }

      const list = Array.isArray(this.currencies) ? this.currencies : []
      const byId = list.find((currency) => currency && currency.id === raw)
      if (byId) {
        return byId.id
      }

      const byCode = list.find(
        (currency) =>
          currency && (currency.short_name === raw || currency.name === raw),
      )
      return byCode ? byCode.id : null
    },
    isUnknownDescriptorValue(field) {
      const rawValue =
        this.localItem?.[field] ?? this.activeItem?.[field] ?? null

      if (rawValue === null || rawValue === undefined) {
        return true
      }

      if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim()
        return trimmed === '' || trimmed === 'Unknown'
      }

      if (typeof rawValue === 'object') {
        if (typeof rawValue.name === 'string') {
          const trimmedName = rawValue.name.trim()
          if (trimmedName === '' || trimmedName === 'Unknown') {
            return true
          }
        }

        return rawValue.id === null || rawValue.id === undefined
      }

      return false
    },
    isDescriptorEditable(field) {
      // ArtefactCard campaign editor keeps descriptor fields editable.
      if (this.forceCampaignLayout) {
        return true
      }

      // ItemCard campaign artefacts should only allow edits when the
      // selected static value is Unknown/blank (or session-overridden).
      const isItemCardCampaignSelection =
        this.effectiveCampaign &&
        !this.forceCampaignLayout &&
        this.hasSelectedCampaignArtefact

      if (isItemCardCampaignSelection) {
        const sessionFlag = this.localItem?.[`_session_${field}_editable`]

        if (sessionFlag === true) {
          return true
        }

        if (sessionFlag === false) {
          return false
        }

        const unknownFlag = this.localItem?.[`_static_${field}_unknown`]

        if (unknownFlag === true) {
          return true
        }

        if (unknownFlag === false) {
          return false
        }

        return this.isUnknownDescriptorValue(field)
      }

      if (!this.isCommittedToDb) {
        return true
      }

      const sessionFlag = this.localItem?.[`_session_${field}_editable`]

      if (sessionFlag === true) {
        return true
      }

      if (sessionFlag === false) {
        return false
      }

      const unknownFlag = this.localItem?.[`_static_${field}_unknown`]

      if (unknownFlag === true) {
        return true
      }

      if (unknownFlag === false) {
        return false
      }

      return this.isUnknownDescriptorValue(field)
    },
  },
  computed: {
    isItemCardMode() {
      return !this.forceCampaignLayout
    },
    isItemCardNonCampaignMode() {
      return this.isItemCardMode && !this.effectiveCampaign
    },
    isItemCardCampaignMode() {
      return this.isItemCardMode && this.effectiveCampaign
    },
    isDroppedNonCampaignArtefactMode() {
      const droppedFlag =
        this.localItem?._dropped_non_campaign_artefact ??
        this.activeItem?._dropped_non_campaign_artefact ??
        this.localItem?._known_non_campaign_artefact ??
        this.activeItem?._known_non_campaign_artefact ??
        false

      return this.isItemCardMode && this.isNewItem && !!droppedFlag
    },
    isExistingItemCardArtefact() {
      return this.isItemCardMode && !this.isNewItem
    },
    isItemCardArtefactTypeEditable() {
      if (this.isExistingItemCardArtefact) {
        return false
      }

      return this.isEditable(this.localItem?.artefact_type)
    },
    isItemCardCampaignArtefactSelected() {
      return this.isItemCardCampaignMode && this.hasSelectedCampaignArtefact
    },
    isArtefactCardCampaignMode() {
      return this.forceCampaignLayout
    },
    selectedArtefactTypeRecord() {
      const records = Array.isArray(this.artefactTypes)
        ? this.artefactTypes
        : []
      if (!records.length) {
        return null
      }

      const raw =
        this.localItem?.artefact_type ?? this.activeItem?.artefact_type
      if (!raw) {
        return null
      }

      const rawId = typeof raw === 'object' ? (raw.id ?? null) : null
      if (rawId) {
        const byId = records.find((record) => record?.id === rawId)
        if (byId) {
          return byId
        }
      }

      const rawName = typeof raw === 'object' ? (raw.name ?? '') : String(raw)
      const normalisedName = rawName.trim().toLowerCase()
      if (!normalisedName) {
        return null
      }

      return (
        records.find(
          (record) =>
            typeof record?.name === 'string' &&
            record.name.trim().toLowerCase() === normalisedName,
        ) ?? null
      )
    },
    filteredItemSizes() {
      const fallbackSizes = Array.isArray(this.itemSizes) ? this.itemSizes : []
      const typeRecord = this.selectedArtefactTypeRecord
      const mappedItemSizes = Array.isArray(typeRecord?.item_sizes)
        ? typeRecord.item_sizes
        : []

      if (!mappedItemSizes.length) {
        return fallbackSizes
      }

      const fallbackById = new Map(
        fallbackSizes.filter((size) => size?.id).map((size) => [size.id, size]),
      )

      return mappedItemSizes.map((size) => fallbackById.get(size.id) || size)
    },
    raceOptions() {
      const options = Array.isArray(this.races) ? [...this.races] : []
      const hasUnknown = options.some(
        (option) =>
          option &&
          option.id === UNKNOWN_RACE_OPTION_ID &&
          option.name === 'Unknown',
      )

      if (!hasUnknown) {
        options.unshift({ id: UNKNOWN_RACE_OPTION_ID, name: 'Unknown' })
      }

      return options
    },
    selectedRaceOption() {
      const selectedRaceId = this.localItem?.race ?? UNKNOWN_RACE_OPTION_ID
      const raceOptions = Array.isArray(this.raceOptions)
        ? this.raceOptions
        : []
      const selectedRace = raceOptions.find((option) => {
        const optionId = option?.id ?? null
        return optionId === selectedRaceId
      })

      if (selectedRace) {
        return selectedRace
      }

      if (selectedRaceId === UNKNOWN_RACE_OPTION_ID || !selectedRaceId) {
        return null
      }

      const activeRace = this.activeItem?.race
      if (
        activeRace &&
        typeof activeRace === 'object' &&
        activeRace.id === selectedRaceId
      ) {
        return activeRace
      }

      return {
        id: selectedRaceId,
        name: this.readonlyRaceName || selectedRaceId,
      }
    },
    raceSelection: {
      get() {
        return this.selectedRaceOption
      },
      set(value) {
        this.onArtefactFieldChange('race', this.reduceObject(value))
      },
    },
    effectiveCampaign() {
      return !!(
        this.forceCampaignLayout ||
        this.isCampaignArtefact ||
        this.localItem?.campaign ||
        this.activeItem?.campaign
      )
    },
    artefactSelectionReady() {
      const item = this.localItem || {}

      return Boolean(item.artefact_type) || this.isExistingItemCardArtefact
    },
    hasSelectedCampaignArtefact() {
      const selectedName =
        this.localItem?.name ?? this.activeItem?.name ?? this.itemName ?? ''

      if (typeof selectedName === 'string') {
        return selectedName.trim().length > 0
      }

      return Boolean(selectedName)
    },
    selectedArtefactTypeName() {
      const raw =
        this.localItem?.artefact_type ?? this.activeItem?.artefact_type ?? ''

      if (typeof raw === 'string') {
        return raw.trim().toLowerCase()
      }

      return String(raw?.name ?? '')
        .trim()
        .toLowerCase()
    },
    isItemCardCampaignArtefactSelection() {
      return (
        this.effectiveCampaign &&
        !this.forceCampaignLayout &&
        this.hasSelectedCampaignArtefact
      )
    },
    isCampaignArmourOrWeaponArtefactSelection() {
      if (!this.isItemCardCampaignArtefactSelection) {
        return false
      }

      return (
        this.selectedArtefactTypeName === 'armour' ||
        this.selectedArtefactTypeName === 'melee weapon' ||
        this.selectedArtefactTypeName === 'ranged weapon'
      )
    },
    showCampaignSelectedPwrStorageField() {
      return (
        this.isItemCardCampaignArtefactSelection &&
        !this.isCampaignArmourOrWeaponArtefactSelection
      )
    },
    isItemCardCampaignShortDescriptionReadonly() {
      return (
        this.isItemCardCampaignArtefactSelection &&
        this.shortDescriptionReadonly
      )
    },
    showDroppedArtefactToggle() {
      return (
        this.isItemCardMode &&
        this.isNewItem &&
        typeof this.onDroppedArtefactToggle === 'function'
      )
    },
    showCampaignArtefactToggle() {
      return (
        this.isItemCardMode &&
        this.isNewItem &&
        !this.isDroppedNonCampaignArtefactMode &&
        typeof this.onCampaignItemToggle === 'function'
      )
    },
    showDroppedArtefactSelect() {
      return (
        this.isDroppedNonCampaignArtefactMode &&
        typeof this.onItemSelect === 'function'
      )
    },
    isCampaignSelectedPwrStorageReadonly() {
      if (!this.isItemCardCampaignArtefactSelection) {
        return false
      }

      if (this.isCampaignArmourOrWeaponArtefactSelection) {
        return true
      }

      // Existing saved campaign artefact items: always readonly
      if (!this.isNewItem) {
        return true
      }

      return this.localItem?._static_pwr_storage_readonly === true
    },
    isEstimatedValueToggleReadonly() {
      if (!this.isItemCardCampaignArtefactSelection) {
        return false
      }

      const estimatedValue = !!(
        this.localItem?.estimated_value ?? this.activeItem?.estimated_value
      )

      return !estimatedValue
    },
    isCampaignArmourOrWeaponBasicPwrStorageHidden() {
      if (!this.forceCampaignLayout) {
        return false
      }

      return (
        this.selectedArtefactTypeName === 'armour' ||
        this.selectedArtefactTypeName === 'melee weapon' ||
        this.selectedArtefactTypeName === 'ranged weapon'
      )
    },
    showValueSection() {
      if (this.effectiveCampaign && !this.forceCampaignLayout) {
        // ItemCard campaign mode: hide value fields until a campaign artefact is selected.
        return this.hasSelectedCampaignArtefact
      }

      // Non-campaign and ArtefactCard campaign layout keep existing behaviour.
      return this.artefactSelectionReady
    },
    showReadonlyItemType() {
      if (!this.isOwnedByCharacter) return false

      return this.showArtefactBaseItemSelect
    },
    showEditableArtefactBaseItemSelect() {
      return (
        this.artefactSelectionReady &&
        this.showArtefactBaseItemSelect &&
        !this.isOwnedByCharacter &&
        !this.isExistingItemCardArtefact
      )
    },
    showReadonlyArtefactBaseItem() {
      return (
        this.artefactSelectionReady &&
        this.showArtefactBaseItemSelect &&
        !this.isOwnedByCharacter &&
        this.isExistingItemCardArtefact
      )
    },
    isOwnedByCharacter() {
      return !!(
        this.activeItem?.character_name ||
        this.activeItem?.player_name ||
        this.localItem?.character_name ||
        this.localItem?.player_name
      )
    },
    readonlyArtefactBaseItemLabel() {
      const rawValue =
        this.localItem?.artefact_base_item ??
        this.activeItem?.artefact_base_item ??
        null

      if (rawValue && typeof rawValue === 'object') {
        return this.getArtefactBaseItemLabel(rawValue)
      }

      const rawId = rawValue ?? null
      const options = Array.isArray(this.artefactBaseItems)
        ? this.artefactBaseItems
        : []
      const normalisedRawId =
        rawId === null || rawId === undefined || rawId === ''
          ? null
          : String(rawId)

      if (normalisedRawId) {
        const selectedOption = options.find(
          (option) => String(option?.id ?? '') === normalisedRawId,
        )
        if (selectedOption) {
          return this.getArtefactBaseItemLabel(selectedOption)
        }
      }

      const getStaticItemById =
        this.$store?.getters?.['characters/getStaticItemById']
      if (typeof getStaticItemById === 'function' && normalisedRawId) {
        const directSelectedItem = getStaticItemById(rawId)
        if (directSelectedItem) {
          return this.getArtefactBaseItemLabel(directSelectedItem)
        }

        const numericRawId = Number(rawId)
        if (!Number.isNaN(numericRawId)) {
          const numericSelectedItem = getStaticItemById(numericRawId)
          if (numericSelectedItem) {
            return this.getArtefactBaseItemLabel(numericSelectedItem)
          }
        }
      }

      if (typeof rawValue === 'string') {
        return rawValue
      }

      return ''
    },
    readonlyItemType() {
      const getStaticItemById =
        this.$store?.getters?.['characters/getStaticItemById']
      const getStaticItemByName =
        this.$store?.getters?.['characters/getStaticItemByName']

      let artefactItem = null

      const directBaseItemId =
        this.localItem?.artefact_base_item ??
        this.activeItem?.artefact_base_item ??
        null
      if (typeof getStaticItemById === 'function' && directBaseItemId) {
        const baseItem = getStaticItemById(directBaseItemId) ?? null
        if (baseItem?.name) return baseItem.name
      }

      const selectedStaticItemId = this.activeItem?.item ?? null
      if (typeof getStaticItemById === 'function' && selectedStaticItemId) {
        artefactItem = getStaticItemById(selectedStaticItemId) ?? null
      }

      if (!artefactItem) {
        const selectedName = this.localItem?.name || this.activeItem?.name || ''
        if (typeof getStaticItemByName === 'function' && selectedName) {
          artefactItem = getStaticItemByName(selectedName) ?? null
        }
      }

      const baseItemId = artefactItem?.artefact_base_item ?? null
      if (typeof getStaticItemById === 'function' && baseItemId) {
        const baseItem = getStaticItemById(baseItemId)
        if (baseItem?.name) return baseItem.name
      }

      return ''
    },
    readonlyItemSizeName() {
      const getItemSizeId = (value) => {
        if (value === null || value === undefined || value === '') {
          return null
        }

        if (typeof value === 'object') {
          return value.id ?? null
        }

        return value
      }

      const selectedId = getItemSizeId(
        this.localItem?.item_size ?? this.activeItem?.item_size,
      )

      const options = Array.isArray(this.filteredItemSizes)
        ? this.filteredItemSizes
        : []

      if (selectedId) {
        const selectedOption = options.find(
          (option) => getItemSizeId(option) === selectedId,
        )
        if (selectedOption) {
          return this.getItemSizeLabel(selectedOption)
        }
      }

      const localValue = this.localItem?.item_size ?? this.activeItem?.item_size
      if (localValue && typeof localValue === 'object') {
        return this.getItemSizeLabel(localValue)
      }

      return ''
    },
    showArtefactBaseItemSelect() {
      const artefactType =
        this.localItem?.artefact_type ?? this.activeItem?.artefact_type ?? ''
      const normalisedArtefactType = String(artefactType).trim().toLowerCase()

      const allowedTypes = this.isItemCardMode
        ? ['ammunition', 'container']
        : ['armour', 'ammunition', 'melee weapon', 'ranged weapon', 'container']

      return allowedTypes.includes(normalisedArtefactType)
    },
    showContainerSizeSelect() {
      const artefactType =
        this.localItem?.artefact_type ?? this.activeItem?.artefact_type ?? ''

      return artefactType === 'Container'
    },
    isCommittedToDb() {
      const id = this.activeItem?.id ?? this.localItem?.id ?? null

      if (id === null || id === undefined || id === 0) {
        return false
      }

      if (typeof id === 'string' && id.includes('TMP')) {
        return false
      }

      return true
    },
    areValueFieldsLocked() {
      const item = this.localItem || {}
      const estimatedValue = !!(
        item.estimated_value ?? this.activeItem?.estimated_value
      )

      if (estimatedValue) {
        return false
      }

      // ItemCard campaign artefacts: values are editable only when the
      // selected campaign artefact has Unknown/blank static values.
      const isItemCardCampaignSelection =
        this.effectiveCampaign &&
        !this.forceCampaignLayout &&
        this.hasSelectedCampaignArtefact

      if (isItemCardCampaignSelection) {
        const unknownLower = this.localItem?._static_value_lower_unknown
        const unknownUpper = this.localItem?._static_value_upper_unknown
        const unknownAggregate = this.localItem?._static_value_unknown

        // If explicit per-field flags are available, lock only when both
        // lower and upper are provided by the selected campaign artefact.
        if (
          typeof unknownLower === 'boolean' ||
          typeof unknownUpper === 'boolean'
        ) {
          const lowerIsKnown = unknownLower === false
          const upperIsKnown = unknownUpper === false
          return lowerIsKnown && upperIsKnown
        }

        if (typeof unknownAggregate === 'boolean') {
          return !unknownAggregate
        }

        return this.isCommittedToDb
      }

      if (this.effectiveCampaign) {
        return this.artefactSelectionReady
      }

      return this.isCommittedToDb
    },
    selectedCurrencyValue() {
      // console.log('selectedCurrencyValue called...')
      // console.log('localItem currency:', this.localItem?.currency)
      // console.log('activeItem currency:', this.activeItem?.currency)
      return this.normaliseCurrencyValue(
        this.localItem?.currency ?? this.activeItem?.currency,
      )
    },
    readonlyCurrencyName() {
      const raw = this.selectedCurrencyValue
      if (raw === null || raw === undefined || raw === '') return ''

      const list = Array.isArray(this.currencies) ? this.currencies : []

      const match = list.find((c) => c && c.id === raw)

      return match ? this.getCurrencyLabel(match) : ''
    },
  },
}
</script>

<style scoped>
.form-floating textarea.bb-textarea-with-label-compact {
  min-height: 7.5em !important;
  height: 7.5em !important;
}
</style>
